/**
 * 공통 코드 1건 — code↔label. 다중 부모 공통 코드(`/common/codes`) 조회 결과의 단위. 셀렉트 옵션·라벨 변환 공용.
 */
export interface CommonCodeItem {
  code: string;
  label: string;
}

/**
 * 공통 코드 정규화 저장소 — 두 인덱스로 유지:
 * - `codes`: 부모코드 → (code → item). **텍스트 변환**용 O(1) 라벨 조회.
 * - `list`:  부모코드 → code[] (서버 순서 보존). **목록/셀렉트** 조립용(코드 순회 → codes 로 라벨 해석).
 */
export interface CommonCodeStore {
  codes: Record<string, Record<string, CommonCodeItem>>;
  list: Record<string, string[]>;
}

/**
 * 복수선택 sentinel — **'선택 없음'**. `[]`(전체)와 구분해야 하는 이유가 여기 있다: 전부 끈 상태를 `[]` 로
 * 커밋하면 그건 '전체'라서 화면이 즉시 전부 켜지고, **사용자가 방금 한 조작이 되돌려진다**.
 * admin `shared/data-types/common.ts` 와 같은 모델(값도 `'none'` 동일) — 두 앱의 필터 어휘를 맞춘다.
 */
export const MULTI_NONE = 'none';

/**
 * 복수선택 세 상태 — `[]` 전체 / 값들 / `MULTI_NONE` 선택 없음.
 *
 * 항목 타입을 제네릭으로 연 이유 — 계측 인버터는 문자 key, **발전량 조회 인버터는 숫자 id**(`statInverterId`)라
 * 같은 세 상태 모델을 쓰면서 항목 타입만 다르다. 판정·정규화 헬퍼를 두 벌로 복제하지 않으려면 여기서 열어야 한다.
 */
export type MultiSelect<T = string> = T[] | typeof MULTI_NONE;

/** 문자 코드 복수선택 — 가장 흔한 형태(코드·key 목록)라 별칭을 유지한다. */
export type MultiSelectValue = MultiSelect<string>;

/** '선택 없음' 여부 — 좁히기(narrowing)용. */
export function isNoneSelected<T>(value: MultiSelect<T>): value is typeof MULTI_NONE {
  return value === MULTI_NONE;
}

/**
 * 값 배열로 정규화 — '선택 없음'을 `[]` 로 접는다. wire 조립처럼 **목록만 필요한** 자리에서 쓴다.
 * ⚠️ 여기서 `[]` 는 '전체'가 아니라 그냥 빈 목록이다 — '선택 없음' 분기(조회 생략)는 `isNoneSelected` 로
 * **미리** 끝내 두고 호출할 것.
 */
export function toCodeList<T>(value: MultiSelect<T>): T[] {
  return isNoneSelected(value) ? [] : value;
}
